import { ECategoryType } from '@/services/doctor/DiagnosisCategoryService';

export type DiagnosisCategory = {
  categoryType: ECategoryType;
  categoryTypeId: number;
};

export type QuestionCategoryType = {
  categoryCode: string;
  categoryName: string;
  isRecommendedTreatment: boolean;
  diagnosisCategory?: DiagnosisCategory | null;
};

export enum EQuestionCategoryId {
  NONE = '0',
  FAMILY_MEDICINE = '1',
  INTERNAL_MEDICINE = '2',
  ANESTHESIOLOGY_AND_PAIN_MEDICINE = '3',
  UROLOGY = '4',
  OBSTETRICS_AND_GYNECOLOGY = '5',
  PLASTIC_SURGERY = '6',
  PEDIATRICS = '7',
  NEUROLOGY = '8',
  NEUROSURGERY = '9',
  OPHTHALMOLOGY = '10',
  RADIOLOGY = '11',
  SURGERY = '12',
  EMERGENCY_MEDICINE = '13',
  OTORHINOLARYNGOLOGY = '14',
  REHABILITATION_MEDICINE = '15',
  PSYCHIATRY = '16',
  ORTHOPEDICS = '17',
  DENTISTRY = '18',
  DERMATOLOGY = '19',
  ORIENTAL_MEDICINE = '20',
  COLD_FLU = '21',
  HYPERTENSION_DIABETES_HYPERLIPIDEMIA = '22',
  MENS_HEALTH = '23',
  DIET = '24',
  CHRONIC_DISEASE = '25',
  WOMENS_HEALTH = '26',
  DIGESTIVE_DISEASE = '27',
  POST_CONTRACEPTION = '28',
  ACNE_SKIN_DISEASE = '29',
  MENTAL_HEALTH = '30',
  CORONA_TREATMENT = '31',
  HAIR_LOSS = '32',
  PAIN = '33',
  PRESCRIPTION_DRUGS = '35',
  NUTRITIONAL_SUPPLEMENTS = '36',
  DIETARY_MEDICINE = '37',
  DIET_CATEGORY = '38',
  OTHER_SYMPTOMS = '39',
}

export const questionCategoryMap: Record<EQuestionCategoryId, QuestionCategoryType> = {
  [EQuestionCategoryId.NONE]: {
    categoryCode: 'NONE',
    categoryName: '없음',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.FAMILY_MEDICINE]: {
    categoryCode: 'FAMILY_MEDICINE',
    categoryName: '가정의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 1,
    },
  },
  [EQuestionCategoryId.INTERNAL_MEDICINE]: {
    categoryCode: 'INTERNAL_MEDICINE',
    categoryName: '내과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 3,
    },
  },
  [EQuestionCategoryId.ANESTHESIOLOGY_AND_PAIN_MEDICINE]: {
    categoryCode: 'ANESTHESIOLOGY_AND_PAIN_MEDICINE',
    categoryName: '마취통증의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 15,
    },
  },
  [EQuestionCategoryId.UROLOGY]: {
    categoryCode: 'UROLOGY',
    categoryName: '비뇨의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 9,
    },
  },
  [EQuestionCategoryId.OBSTETRICS_AND_GYNECOLOGY]: {
    categoryCode: 'OBSTETRICS_AND_GYNECOLOGY',
    categoryName: '산부인과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 10,
    },
  },
  [EQuestionCategoryId.PLASTIC_SURGERY]: {
    categoryCode: 'PLASTIC_SURGERY',
    categoryName: '성형외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.PEDIATRICS]: {
    categoryCode: 'PEDIATRICS',
    categoryName: '소아청소년과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 2,
    },
  },
  [EQuestionCategoryId.NEUROLOGY]: {
    categoryCode: 'NEUROLOGY',
    categoryName: '신경과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 8,
    },
  },
  [EQuestionCategoryId.NEUROSURGERY]: {
    categoryCode: 'NEUROSURGERY',
    categoryName: '신경외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.OPHTHALMOLOGY]: {
    categoryCode: 'OPHTHALMOLOGY',
    categoryName: '안과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 17,
    },
  },
  [EQuestionCategoryId.RADIOLOGY]: {
    categoryCode: 'RADIOLOGY',
    categoryName: '영상의학과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.SURGERY]: {
    categoryCode: 'SURGERY',
    categoryName: '외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.EMERGENCY_MEDICINE]: {
    categoryCode: 'EMERGENCY_MEDICINE',
    categoryName: '응급의학과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.OTORHINOLARYNGOLOGY]: {
    categoryCode: 'OTORHINOLARYNGOLOGY',
    categoryName: '이비인후과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 18,
    },
  },
  [EQuestionCategoryId.REHABILITATION_MEDICINE]: {
    categoryCode: 'REHABILITATION_MEDICINE',
    categoryName: '재활의학과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.PSYCHIATRY]: {
    categoryCode: 'PSYCHIATRY',
    categoryName: '정신건강의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 7,
    },
  },
  [EQuestionCategoryId.ORTHOPEDICS]: {
    categoryCode: 'ORTHOPEDICS',
    categoryName: '정형외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.DENTISTRY]: {
    categoryCode: 'DENTISTRY',
    categoryName: '치의학',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.DERMATOLOGY]: {
    categoryCode: 'DERMATOLOGY',
    categoryName: '피부과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 6,
    },
  },
  [EQuestionCategoryId.ORIENTAL_MEDICINE]: {
    categoryCode: 'ORIENTAL_MEDICINE',
    categoryName: '한의학',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 19,
    },
  },
  [EQuestionCategoryId.COLD_FLU]: {
    categoryCode: 'COLD_FLU',
    categoryName: '감기/몸살',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 1,
    },
  },
  [EQuestionCategoryId.HYPERTENSION_DIABETES_HYPERLIPIDEMIA]: {
    categoryCode: 'HYPERTENSION_DIABETES_HYPERLIPIDEMIA',
    categoryName: '고혈압/당뇨/고지혈증',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.MENS_HEALTH]: {
    categoryCode: 'MENS_HEALTH',
    categoryName: '남성질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 606,
    },
  },
  [EQuestionCategoryId.DIET]: {
    categoryCode: 'DIET',
    categoryName: '다이어트',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 12,
    },
  },
  [EQuestionCategoryId.CHRONIC_DISEASE]: {
    categoryCode: 'CHRONIC_DISEASE',
    categoryName: '만성질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 5,
    },
  },
  [EQuestionCategoryId.WOMENS_HEALTH]: {
    categoryCode: 'WOMENS_HEALTH',
    categoryName: '여성질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 586,
    },
  },
  [EQuestionCategoryId.DIGESTIVE_DISEASE]: {
    categoryCode: 'DIGESTIVE_DISEASE',
    categoryName: '소화기질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 577,
    },
  },
  [EQuestionCategoryId.POST_CONTRACEPTION]: {
    categoryCode: 'POST_CONTRACEPTION',
    categoryName: '사후피임약',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.ACNE_SKIN_DISEASE]: {
    categoryCode: 'ACNE_SKIN_DISEASE',
    categoryName: '여드름/피부염',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 11,
    },
  },
  [EQuestionCategoryId.MENTAL_HEALTH]: {
    categoryCode: 'MENTAL_HEALTH',
    categoryName: '정신건강',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 589,
    },
  },
  [EQuestionCategoryId.CORONA_TREATMENT]: {
    categoryCode: 'CORONA_TREATMENT',
    categoryName: '코로나 치료',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 14,
    },
  },
  [EQuestionCategoryId.HAIR_LOSS]: {
    categoryCode: 'HAIR_LOSS',
    categoryName: '탈모',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 9,
    },
  },
  [EQuestionCategoryId.PAIN]: {
    categoryCode: 'PAIN',
    categoryName: '통증',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 6,
    },
  },
  [EQuestionCategoryId.PRESCRIPTION_DRUGS]: {
    categoryCode: 'PRESCRIPTION_DRUGS',
    categoryName: '처방약',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.NUTRITIONAL_SUPPLEMENTS]: {
    categoryCode: 'NUTRITIONAL_SUPPLEMENTS',
    categoryName: '영양제',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.DIETARY_MEDICINE]: {
    categoryCode: 'DIETARY_MEDICINE',
    categoryName: '다이어트 한약',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.DIET_CATEGORY]: {
    categoryCode: 'DIET',
    categoryName: '식단',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
  [EQuestionCategoryId.OTHER_SYMPTOMS]: {
    categoryCode: 'OTHER_SYMPTOMS',
    categoryName: '기타증상',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  },
} as const;
